/**
 * Arc Diagram - Observable HQ pattern implementation
 * Linear arrangement with curved relationship arcs
 * https://observablehq.com/@d3/arc-diagram
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography } from '@mui/material';
import useTokenExaminerStore from '../../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../../utils/faanaScoring';

// eslint-disable-next-line max-lines-per-function
function ArcDiagram() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    _relationships,
    entities,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render arc diagram
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || !filteredRelationships.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 40, right: 40, bottom: 40, left: 40 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const container = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Prepare nodes data
    const nodesMap = new Map();
    Array.from(entities.values()).forEach(entity => {
      nodesMap.set(entity.crossEntityHash, {
        id: entity.crossEntityHash,
        name: entity.name,
        type: entity.type,
        connectionCount: 0
      });
    });

    // Count connections
    filteredRelationships.forEach(rel => {
      const sourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
      const targetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;

      if (nodesMap.has(sourceId)) {
        nodesMap.get(sourceId).connectionCount++;
      }
      if (nodesMap.has(targetId)) {
        nodesMap.get(targetId).connectionCount++;
      }
    });

    const nodes = Array.from(nodesMap.values())
      .sort((a, b) => b.connectionCount - a.connectionCount);

    // Position nodes linearly along bottom
    const nodeSpacing = width / (nodes.length + 1);
    nodes.forEach((node, i) => {
      node.x = nodeSpacing * (i + 1);
      node.y = height - 50;
    });

    // Create position lookup
    const nodePositions = new Map();
    nodes.forEach(node => {
      nodePositions.set(node.id, { x: node.x, y: node.y });
    });

    // Prepare links with arc paths
    const links = filteredRelationships
      .map(rel => {
        const sourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
        const targetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
        const sourcePos = nodePositions.get(sourceId);
        const targetPos = nodePositions.get(targetId);

        if (!sourcePos || !targetPos) return null;

        const distance = Math.abs(targetPos.x - sourcePos.x);
        const arcHeight = Math.min(distance * 0.5, height * 0.6);

        return {
          id: rel.id,
          source: sourcePos,
          target: targetPos,
          health: rel.overallHealth || 50,
          confidence: rel.confidence,
          relationshipType: rel.relationshipType,
          arcHeight,
          distance
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.distance - b.distance); // Draw shorter arcs first

    // Create arc path generator
    const arcPath = (d) => {
      const midX = (d.source.x + d.target.x) / 2;
      const controlY = d.source.y - d.arcHeight;

      return `M ${d.source.x} ${d.source.y} Q ${midX} ${controlY} ${d.target.x} ${d.target.y}`;
    };

    // Draw arcs
    const _linkElements = container.selectAll('.arc')
      .data(links)
      .enter().append('path')
      .attr('class', 'arc')
      .attr('d', arcPath)
      .attr('fill', 'none')
      .attr('stroke', d => getFaanaHealthColor(d.health))
      .attr('stroke-width', d => Math.max(1, d.health / 25))
      .attr('opacity', 0.7)
      .on('mouseenter', function(event, d) {
        d3.select(this)
          .attr('stroke-width', Math.max(3, d.health / 15))
          .attr('opacity', 1);

        // Show tooltip
        const tooltip = container.append('g')
          .attr('class', 'tooltip')
          .attr('transform', `translate(${(d.source.x + d.target.x) / 2}, ${d.source.y - d.arcHeight})`);

        const rect = tooltip.append('rect')
          .attr('fill', 'rgba(0,0,0,0.8)')
          .attr('rx', 4);

        const text = tooltip.append('text')
          .attr('fill', 'white')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .style('font-size', '12px')
          .text(`${d.relationshipType} (Health: ${d.health})`);

        const bbox = text.node().getBBox();
        rect.attr('x', bbox.x - 4)
            .attr('y', bbox.y - 2)
            .attr('width', bbox.width + 8)
            .attr('height', bbox.height + 4);
      })
      .on('mouseleave', function(event, d) {
        d3.select(this)
          .attr('stroke-width', Math.max(1, d.health / 25))
          .attr('opacity', 0.7);

        container.selectAll('.tooltip').remove();
      });

    // Draw nodes
    const nodeElements = container.selectAll('.node')
      .data(nodes)
      .enter().append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`);

    nodeElements.append('circle')
      .attr('r', d => 8 + Math.sqrt(d.connectionCount) * 2)
      .attr('fill', '#2196F3')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    nodeElements.append('text')
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(d => d.name.length > 12 ? d.name.substring(0, 10) + '...' : d.name);

    // Add connection count badges
    nodeElements.append('circle')
      .attr('cx', 12)
      .attr('cy', -12)
      .attr('r', 8)
      .attr('fill', '#FF5722')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    nodeElements.append('text')
      .attr('x', 12)
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.connectionCount);

  }, [dimensions, filteredRelationships, entities]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          p: 2,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Arc Diagram
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Nodes sorted by connection count
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Arc height = relationship distance
        </Typography>
        <Typography variant="caption" display="block">
          • Color = relationship health
        </Typography>
      </Paper>
    </Box>
  );
}

export default ArcDiagram;